import { useLocation } from 'react-router-dom';

import { Box } from '@cast/design-system';
import { useViewedOrganization } from '@cast/infrastructure-organization';
import { OrganizationType } from '@cast/types';
import { useAbility } from '@cast/shared-ability-data-access';
import { useShouldLimitConsoleForReporting } from '@cast/shared-trial-ui';
import {
  isKentManagedCluster,
  useClusters,
} from '@cast/shared-cluster-context-data-access';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { PRODUCT_BASE_PATHS } from '@cast/console-constants';

import { useClustersWithApa } from 'features/apa/hooks/useClustersWithApa';
import { buildDboRoute } from 'features/db-optimization/v2/routes';
import { useIsHideDiscountsFeatureEnabled } from 'hooks/permissions';
import { DBO_ROUTE_CONFIG } from 'pages/db-optimization/v2/DbOptimizationControllerV2/constants';

import {
  BASE_PATHS,
  SETTINGS_PATHS,
  buildAccessControlUrl,
} from '@oneclick/constants/navigation';
import { useIsViewingEnterpriseOrg } from '@oneclick/layout/_hooks/useIsViewingEnterpriseOrg';
import { ACCESS_CONTROL_ROUTE_CONFIG } from '@oneclick/pages/access-control/accessControlRouteConfig';
import { flattenNavItems, useVisibleNavItems } from '@oneclick/routing';

import { canViewNavItem } from './canViewNavItem';
import { isHiddenUnderReportingLimit } from './reportingLimitedNav';
import { SidebarDivider } from './SidebarDivider';
import { SidebarItem } from './SidebarItem';
import { SidebarSection } from './SidebarSection';
import { SidebarTreeGroup } from './SidebarTreeGroup';
import {
  getEnterpriseSettingsChildren,
  isOrgCostMonitoringActive,
  isOrgFleetItemActive,
  ORG_APA_SECTION,
  ORG_DBO_SECTION,
  ORG_SIDEBAR_SECTION_TITLES,
  orgApaChildren,
  orgCostManagementChildren,
  orgCostMonitoringChildren,
  orgDboChildren,
  orgExtraFeaturesItems,
  orgFleetItems,
  orgSchedulesChildren,
  orgSettingsChildren,
  orgSidebarIcons,
  orgSsoChild,
  resolveOrgFleetItemPath,
} from './sidebarNavConfig';
import { sidebarItemListSx } from './sidebarStyles';
import { useSidebarSectionExpanded } from './useSidebarSectionExpanded';

const useDboPermissions = () => {
  const ability = useAbility();
  const canViewDbo = ability.canOneOf('view', ['OrganizationDbOptimizer']);
  const isDboV2Enabled = ability.can('view', 'FeatureDboV2');
  const firstDboRoute = DBO_ROUTE_CONFIG.organization[0];
  return {
    canViewDbo,
    isDboV2Enabled,
    firstDboRoute,
  };
};

export const OrgLevelSidebar = () => {
  const { pathname } = useLocation();
  const ability = useAbility();
  const { hasClustersWithApa } = useClustersWithApa();
  const isViewingEnterpriseOrg = useIsViewingEnterpriseOrg();
  const viewedOrganization = useViewedOrganization();
  const { currentOrganization } = useOrganizations();
  const { clusters } = useClusters();
  const hideDiscounts = useIsHideDiscountsFeatureEnabled();
  const { canViewDbo, isDboV2Enabled, firstDboRoute } = useDboPermissions();
  const limitAutomationConsole = useShouldLimitConsoleForReporting();
  const visibleAccessControlNav = useVisibleNavItems(
    ACCESS_CONTROL_ROUTE_CONFIG
  );

  const organizationId = currentOrganization?.id;
  const orgConditions = { organizationId };

  const kentClustersOnly =
    clusters.length > 0 &&
    clusters.every((cluster) => isKentManagedCluster(cluster));

  const visibleSchedulesChildren = orgSchedulesChildren.filter((child) => {
    if (isHiddenUnderReportingLimit(limitAutomationConsole, child)) {
      return false;
    }
    if (child.id === 'scheduledRebalancing' && kentClustersOnly) {
      return false;
    }
    return canViewNavItem(ability, child.rules, orgConditions);
  });

  const visibleCostMonitoringChildren = orgCostMonitoringChildren.filter(
    (child) =>
      !isHiddenUnderReportingLimit(limitAutomationConsole, child) &&
      canViewNavItem(ability, child.rules, orgConditions)
  );

  const visibleCostManagementChildren = orgCostManagementChildren.filter(
    (child) => {
      if (
        hideDiscounts &&
        (child.id === 'commitments' || child.id === 'priceAdjustments')
      ) {
        return false;
      }
      return (
        !isHiddenUnderReportingLimit(limitAutomationConsole, child) &&
        canViewNavItem(ability, child.rules, orgConditions)
      );
    }
  );

  const isViewingChildOrg = viewedOrganization?.type === OrganizationType.CHILD;

  const visibleSettingsChildren = orgSettingsChildren.filter(
    (child) =>
      !(isViewingChildOrg && child.id === 'featureControl') &&
      !isHiddenUnderReportingLimit(limitAutomationConsole, child) &&
      canViewNavItem(ability, child.rules, orgConditions)
  );

  const visibleAccessControlChildren = flattenNavItems(visibleAccessControlNav)
    .filter((item) => item.id)
    .map((item) => ({
      id: item.id!,
      title: item.title,
      path: buildAccessControlUrl(item.route),
    }));

  const canViewSso = canViewNavItem(ability, orgSsoChild.rules, orgConditions);

  const hasVisibleOrgSettingsChildren =
    visibleSettingsChildren.length > 0 ||
    visibleAccessControlChildren.length > 0 ||
    canViewSso;

  const isAnyDboChildActive = pathname.startsWith(BASE_PATHS.dbo);
  const isAnyApaChildActive = pathname.startsWith(BASE_PATHS.apa);
  const isAnySchedulesChildActive = visibleSchedulesChildren.some((child) =>
    pathname.startsWith(child.path)
  );
  const isAnyCostMonitoringActive = isOrgCostMonitoringActive(pathname);
  const isAnyCostManagementActive = visibleCostManagementChildren.some(
    (child) => pathname === child.path || pathname.startsWith(`${child.path}/`)
  );
  const isOnEnterpriseSettings = pathname.includes('/organization/enterprise/');
  const isAnySettingsActive =
    isViewingEnterpriseOrg ||
    isOnEnterpriseSettings ||
    pathname.startsWith(SETTINGS_PATHS.overview) ||
    pathname.startsWith(SETTINGS_PATHS.platformUsage) ||
    pathname.startsWith(SETTINGS_PATHS.featureControl) ||
    pathname.startsWith(SETTINGS_PATHS.cloudIntegrations) ||
    pathname.startsWith(SETTINGS_PATHS.componentControl) ||
    pathname.startsWith(SETTINGS_PATHS.permissionsCenter) ||
    pathname.startsWith(BASE_PATHS.accessControl) ||
    pathname.startsWith(BASE_PATHS.ssoConnections);

  const { expanded: dboExpanded, onToggle: onToggleDbo } =
    useSidebarSectionExpanded(isAnyDboChildActive, pathname);
  const { expanded: apaExpanded, onToggle: onToggleApa } =
    useSidebarSectionExpanded(isAnyApaChildActive, pathname);
  const { expanded: schedulesExpanded, onToggle: onToggleSchedules } =
    useSidebarSectionExpanded(isAnySchedulesChildActive, pathname);
  const { expanded: costMonitoringExpanded, onToggle: onToggleCostMonitoring } =
    useSidebarSectionExpanded(isAnyCostMonitoringActive, pathname);
  const { expanded: costManagementExpanded, onToggle: onToggleCostManagement } =
    useSidebarSectionExpanded(isAnyCostManagementActive, pathname);
  const { expanded: orgSettingsExpanded, onToggle: onToggleOrgSettings } =
    useSidebarSectionExpanded(isAnySettingsActive, pathname);

  const canViewLogs = canViewNavItem(
    ability,
    [['view', 'OrganizationAuditLog']],
    orgConditions
  );

  const visibleApaChildren = orgApaChildren.filter((child) => {
    if (isHiddenUnderReportingLimit(limitAutomationConsole, child)) {
      return false;
    }
    if (!canViewNavItem(ability, child.rules)) {
      return false;
    }
    if (child.requiresClustersWithApa && !hasClustersWithApa) {
      return false;
    }
    return true;
  });

  const visibleDboChildren = orgDboChildren.filter(
    (child) =>
      !isHiddenUnderReportingLimit(limitAutomationConsole, child) &&
      canViewNavItem(ability, child.rules)
  );

  const firstSchedulesPath = visibleSchedulesChildren[0]?.path;
  const firstCostMonitoringPath = visibleCostMonitoringChildren[0]?.path;
  const firstCostManagementPath = visibleCostManagementChildren[0]?.path;
  const firstApaPath = visibleApaChildren[0]?.path;
  const firstDboPath = visibleDboChildren[0]
    ? `${BASE_PATHS.dbo}/${visibleDboChildren[0].segment}`
    : undefined;
  const firstOrgSettingsPath =
    visibleSettingsChildren[0]?.path ??
    visibleAccessControlChildren[0]?.path ??
    (canViewSso ? orgSsoChild.path : undefined);

  if (isViewingEnterpriseOrg && viewedOrganization?.id) {
    const enterpriseSettingsChildren = getEnterpriseSettingsChildren(
      viewedOrganization.id
    ).filter((child) => canViewNavItem(ability, child.rules, orgConditions));
    const firstEnterpriseSettingsPath = enterpriseSettingsChildren[0]?.path;

    return (
      <Box sx={sidebarItemListSx}>
        <SidebarItem
          title={ORG_SIDEBAR_SECTION_TITLES.organizationSettings}
          icon={orgSidebarIcons.orgSettings}
          to={firstEnterpriseSettingsPath}
          expanded={orgSettingsExpanded}
          indicated={isAnySettingsActive}
          showCaret
          onToggle={onToggleOrgSettings}
        />

        {orgSettingsExpanded ? (
          <SidebarTreeGroup>
            {enterpriseSettingsChildren.map((child) => (
              <SidebarItem
                key={child.id}
                title={child.title}
                to={child.path}
                active={
                  pathname === child.path ||
                  pathname.startsWith(`${child.path}/`)
                }
                icon={orgSidebarIcons[child.icon]}
                isNested
              />
            ))}
          </SidebarTreeGroup>
        ) : null}
      </Box>
    );
  }

  const renderSchedulesSection = () => {
    if (visibleSchedulesChildren.length === 0) {
      return null;
    }

    if (visibleSchedulesChildren.length === 1) {
      const [child] = visibleSchedulesChildren;
      return (
        <SidebarItem
          key={child.id}
          title={
            child.id === 'clusterHibernation'
              ? 'Scheduled Hibernation'
              : child.title
          }
          to={child.path}
          active={pathname === child.path}
          icon={orgSidebarIcons[child.id]}
        />
      );
    }

    return (
      <>
        <SidebarItem
          title={ORG_SIDEBAR_SECTION_TITLES.clusterSchedules}
          icon={orgSidebarIcons.clusterSchedules}
          to={firstSchedulesPath}
          expanded={schedulesExpanded}
          indicated={isAnySchedulesChildActive}
          showCaret
          onToggle={onToggleSchedules}
        />

        {schedulesExpanded ? (
          <SidebarTreeGroup>
            {visibleSchedulesChildren.map((child) => (
              <SidebarItem
                key={child.id}
                title={child.title}
                to={child.path}
                active={pathname === child.path}
                icon={orgSidebarIcons[child.id]}
                isNested
              />
            ))}
          </SidebarTreeGroup>
        ) : null}
      </>
    );
  };

  return (
    <>
      <SidebarSection>{ORG_SIDEBAR_SECTION_TITLES.fleet}</SidebarSection>

      <Box sx={sidebarItemListSx}>
        {orgFleetItems.map((item) => {
          if (!canViewNavItem(ability, item.rules)) {
            return null;
          }

          return (
            <SidebarItem
              key={item.id}
              title={item.title}
              to={resolveOrgFleetItemPath(item.id)}
              active={isOrgFleetItemActive(item.id, pathname)}
              icon={orgSidebarIcons[item.id]}
            />
          );
        })}
        {!(
          limitAutomationConsole && ORG_DBO_SECTION.hiddenWhenReportingLimited
        ) && canViewDbo ? (
          isDboV2Enabled && firstDboRoute?.route ? (
            <SidebarItem
              title={firstDboRoute.title}
              to={buildDboRoute(firstDboRoute.route)}
              active={pathname.startsWith(PRODUCT_BASE_PATHS.dbo)}
              icon={orgSidebarIcons.databases}
            />
          ) : (
            <>
              <SidebarItem
                title={ORG_DBO_SECTION.title}
                icon={orgSidebarIcons[ORG_DBO_SECTION.icon]}
                to={firstDboPath}
                expanded={dboExpanded}
                indicated={isAnyDboChildActive}
                showCaret
                onToggle={onToggleDbo}
              />
              {dboExpanded ? (
                <SidebarTreeGroup>
                  {visibleDboChildren.map((child) => {
                    const linkPath = `${BASE_PATHS.dbo}/${child.segment}`;

                    return (
                      <SidebarItem
                        key={child.id}
                        title={child.title}
                        to={linkPath}
                        active={pathname.startsWith(linkPath)}
                        icon={orgSidebarIcons[child.icon]}
                        isNested
                      />
                    );
                  })}
                </SidebarTreeGroup>
              ) : null}
            </>
          )
        ) : null}

        {!(
          limitAutomationConsole && ORG_APA_SECTION.hiddenWhenReportingLimited
        ) ? (
          <>
            <SidebarItem
              title={ORG_APA_SECTION.title}
              icon={orgSidebarIcons[ORG_APA_SECTION.icon]}
              to={firstApaPath}
              expanded={apaExpanded}
              indicated={isAnyApaChildActive}
              showCaret
              onToggle={onToggleApa}
            />
            {apaExpanded ? (
              <SidebarTreeGroup>
                {visibleApaChildren.map((child) => (
                  <SidebarItem
                    key={child.id}
                    title={child.title}
                    to={child.path}
                    active={pathname.startsWith(child.path)}
                    isNested
                  />
                ))}
              </SidebarTreeGroup>
            ) : null}
          </>
        ) : null}
      </Box>

      <SidebarDivider />

      <Box sx={sidebarItemListSx}>
        {renderSchedulesSection()}

        {visibleCostMonitoringChildren.length > 0 ? (
          <>
            <SidebarItem
              title={ORG_SIDEBAR_SECTION_TITLES.costMonitoring}
              icon={orgSidebarIcons.costMonitoring}
              to={firstCostMonitoringPath}
              expanded={costMonitoringExpanded}
              indicated={isAnyCostMonitoringActive}
              showCaret
              onToggle={onToggleCostMonitoring}
            />

            {costMonitoringExpanded ? (
              <SidebarTreeGroup>
                {visibleCostMonitoringChildren.map((child) => (
                  <SidebarItem
                    key={child.id}
                    title={child.title}
                    to={child.path}
                    active={pathname.startsWith(child.path)}
                    icon={orgSidebarIcons[child.id]}
                    isNested
                  />
                ))}
              </SidebarTreeGroup>
            ) : null}
          </>
        ) : null}

        {visibleCostManagementChildren.length > 0 ? (
          <>
            <SidebarItem
              title={ORG_SIDEBAR_SECTION_TITLES.costManagement}
              icon={orgSidebarIcons.costManagement}
              to={firstCostManagementPath}
              expanded={costManagementExpanded}
              indicated={isAnyCostManagementActive}
              showCaret
              onToggle={onToggleCostManagement}
            />

            {costManagementExpanded ? (
              <SidebarTreeGroup>
                {visibleCostManagementChildren.map((child) => (
                  <SidebarItem
                    key={child.id}
                    title={child.title}
                    to={child.path}
                    active={
                      pathname === child.path ||
                      pathname.startsWith(`${child.path}/`)
                    }
                    icon={orgSidebarIcons[child.id]}
                    isNested
                  />
                ))}
              </SidebarTreeGroup>
            ) : null}
          </>
        ) : null}

        {orgExtraFeaturesItems.map((item) => {
          if (isHiddenUnderReportingLimit(limitAutomationConsole, item)) {
            return null;
          }
          if (!canViewNavItem(ability, item.rules, orgConditions)) {
            return null;
          }

          return (
            <SidebarItem
              key={item.id}
              title={item.title}
              to={item.path}
              active={pathname.startsWith(item.basePath)}
              icon={orgSidebarIcons[item.icon]}
            />
          );
        })}
      </Box>

      <SidebarDivider />

      <Box sx={sidebarItemListSx}>
        {canViewLogs ? (
          <SidebarItem
            title={ORG_SIDEBAR_SECTION_TITLES.logs}
            to={BASE_PATHS.eventLog}
            active={pathname.startsWith(BASE_PATHS.eventLog)}
            icon={orgSidebarIcons.logs}
          />
        ) : null}

        {hasVisibleOrgSettingsChildren ? (
          <>
            <SidebarItem
              title={ORG_SIDEBAR_SECTION_TITLES.organizationSettings}
              icon={orgSidebarIcons.orgSettings}
              to={firstOrgSettingsPath}
              expanded={orgSettingsExpanded}
              indicated={isAnySettingsActive}
              showCaret
              onToggle={onToggleOrgSettings}
            />

            {orgSettingsExpanded ? (
              <SidebarTreeGroup>
                {visibleSettingsChildren.map((child) => (
                  <SidebarItem
                    key={child.id}
                    title={child.title}
                    to={child.path}
                    active={
                      pathname === child.path ||
                      pathname.startsWith(`${child.path}/`)
                    }
                    icon={orgSidebarIcons[child.id]}
                    isNested
                  />
                ))}
                {visibleAccessControlChildren.map((child) => (
                  <SidebarItem
                    key={child.id}
                    title={child.title}
                    to={child.path}
                    active={pathname === child.path}
                    icon={orgSidebarIcons[child.id]}
                    isNested
                  />
                ))}
                {canViewSso ? (
                  <SidebarItem
                    key={orgSsoChild.id}
                    title={orgSsoChild.title}
                    to={orgSsoChild.path}
                    active={
                      pathname === orgSsoChild.path ||
                      pathname.startsWith(`${orgSsoChild.path}/`)
                    }
                    icon={orgSidebarIcons[orgSsoChild.id]}
                    isNested
                  />
                ) : null}
              </SidebarTreeGroup>
            ) : null}
          </>
        ) : null}
      </Box>
    </>
  );
};
